import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  asObject,
  Datastore,
  DatastoreDocument,
  RequestStatus,
} from '@freelancer/datastore';
import {
  FreelancerReputation,
  ProfileViewUsersCollection,
  Review,
  ReviewsCollection,
  ReviewSkill,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { AnchorScroll } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { CalloutPlacement } from '@freelancer/ui/callout';
import { DropdownFilterContentSize } from '@freelancer/ui/dropdown-filter';
import { trackById } from '@freelancer/ui/helpers';
import { LinkColor } from '@freelancer/ui/link';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import {
  FontColor,
  FontStyle,
  FontType,
  FontWeight,
  TextSize,
} from '@freelancer/ui/text';
import {
  ToastAlertColor,
  ToastAlertService,
  ToastAlertType,
} from '@freelancer/ui/toast-alert';
import { RoleApi } from 'api-typings/common/common';
import { ReviewTypeApi } from 'api-typings/projects/projects';
import { ReviewItem } from 'app/projects/project-view/project-view-reviews/review-area/review-area.type';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  map,
  mapTo,
  startWith,
  take,
} from 'rxjs/operators';
import { UserProfileSection } from '../user-profile.component';

export interface SkillFilterItem {
  readonly displayName: string;
  readonly controlName: string;
}

export interface ReviewFilters {
  limit: number;
  skills: readonly ReviewSkill[];
}

enum FeaturedReviewAction {
  FEATURE,
  UNFEATURE,
}

@Component({
  selector: 'app-user-profile-reviews',
  template: `
    <fl-card>
      <fl-card-header-title i18n="Review heading">
        Reviews
      </fl-card-header-title>

      <fl-card-header-right>
        <ng-container
          *ngIf="isLoggedIn$ | flAsync; else loggedOutViewProfileLink"
        >
          <app-user-profile-reviews-filter
            *ngIf="showFiltersDropdown$ | flAsync"
            [flHideMobile]="true"
            [usersId$]="usersId$"
            [viewProfileAs$]="viewProfileAs$"
            (filtersSelected)="handleFilterItemsSelected($event)"
          ></app-user-profile-reviews-filter>
        </ng-container>
      </fl-card-header-right>

      <fl-banner-alert
        *ngIf="showSavingFeaturedReviewError"
        i18n="Failed To Save Changes"
        [type]="BannerAlertType.ERROR"
        [flMarginBottom]="Margin.SMALL"
      >
        Failed to save changes. Please try again or
        <fl-link flTrackingLabel="GoToContactSupport" [link]="'/contact'">
          contact support with this request ID:
          {{ errorRequestId }}
        </fl-link>
      </fl-banner-alert>

      <fl-toast-alert
        i18n="Success Message Of Saving Featured Review"
        [id]="SAVING_FEATURED_REVIEW_SUCCESS_MESSAGE"
        [color]="ToastAlertColor.LIGHT"
        [type]="ToastAlertType.SUCCESS"
      >
        Changes saved
      </fl-toast-alert>

      <!--Review list section-->
      <ng-container *ngIf="!(reviewsStatus$ | flAsync)?.error">
        <ng-container *ngIf="reviewItems$ | flAsync as reviewItems">
          <ng-container *ngIf="reviewsCount$ | flAsync as reviewCount">
            <fl-text
              *ngIf="selectedSkillFilters$ | flAsync as selectedSkills"
              [color]="FontColor.MID"
              [flMarginBottom]="Margin.SMALL"
            >
              <ng-container
                *ngIf="reviewsReady$ | flAsync; else loadingReviews"
              >
                <ng-container
                  *ngIf="selectedSkills.length > 0; else reviewsUnfiltered"
                  i18n="
                    Information about the reviews count and selected filters
                  "
                >
                  Showing {{ usersViewSeenReviewsCount$ | flAsync }} -
                  {{ reviewsOnPageCount$ | flAsync }} out of
                  {{ approxTotalReviewsCount$ | flAsync }} reviews for
                </ng-container>

                <ng-template #reviewsUnfiltered>
                  <ng-container i18n="Information about the reviews count">
                    Showing {{ usersViewSeenReviewsCount$ | flAsync }} -
                    {{ reviewsOnPageCount$ | flAsync }} out of
                    {{ approxTotalReviewsCount$ | flAsync }} reviews
                  </ng-container>
                </ng-template>
                <fl-text
                  *ngFor="
                    let skill of selectedSkills;
                    first as isFirst;
                    last as isLast;
                    trackBy: trackById
                  "
                  [color]="FontColor.MID"
                  [fontType]="FontType.SPAN"
                  [style]="FontStyle.ITALIC"
                >
                  {{ (isFirst ? '"' : '') + skill.name + (isLast ? '"' : ',') }}
                </fl-text>
              </ng-container>

              <ng-template #loadingReviews>
                <ng-container i18n="Loading reviews message">
                  Loading reviews
                </ng-container>
              </ng-template>
            </fl-text>

            <fl-bit
              *ngIf="showFiltersDropdown$ | flAsync"
              [flShowMobile]="true"
              [flMarginBottom]="Margin.MID"
            >
              <app-user-profile-reviews-filter
                [usersId$]="usersId$"
                [viewProfileAs$]="viewProfileAs$"
                (filtersSelected)="handleFilterItemsSelected($event)"
              ></app-user-profile-reviews-filter>
            </fl-bit>

            <!--Has reviews section-->
            <ng-container
              *ngIf="(reviewsReady$ | flAsync) && !(isScrolling$ | flAsync)"
            >
              <fl-list
                [flMarginBottom]="Margin.SMALL"
                [outerPadding]="false"
                [padding]="ListItemPadding.SMALL"
              >
                <fl-list-item
                  *ngFor="
                    let reviewItem of sliceReviews(reviewItems);
                    trackBy: trackByReviewItem
                  "
                >
                  <app-review-item
                    *ngIf="reviewItem.review && reviewItem.fromUser"
                    [isCorporateProfile]="isCorporateProfile"
                    [isFeaturedReviewEditMode]="isFeaturedReviewEditMode"
                    [isProfileOwner]="isProfileOwner"
                    [review]="reviewItem.review"
                    [reviewer]="reviewItem.fromUser"
                    [showLinks]="true"
                    (featuredReviewClick)="handleFeaturedReviewClicked($event)"
                  ></app-review-item>
                </fl-list-item>
              </fl-list>

              <ng-container
                *ngIf="isLoggedIn$ | flAsync; else loggedOutViewProfileLink"
              >
                <fl-bit
                  *ngIf="reviewItems.length > reviewsPerPage"
                  class="Pagination"
                >
                  <fl-pagination
                    [flHideMobile]="true"
                    [currentPage]="page$ | flAsync"
                    [extremeties]="false"
                    [flMarginBottom]="Margin.MID"
                    [pages]="getPagesCount(reviewCount)"
                    (pageSelected)="onPageSelected($event)"
                  ></fl-pagination>
                  <fl-pagination
                    [flShowMobile]="true"
                    [compact]="true"
                    [currentPage]="page$ | flAsync"
                    [flMarginBottom]="Margin.MID"
                    [labelOptions]="{
                      pageSize: reviewsPerPage,
                      total: reviewItems.length
                    }"
                    [pages]="getPagesCount(reviewCount)"
                    (pageSelected)="onPageSelected($event)"
                  ></fl-pagination>
                </fl-bit>
              </ng-container>
            </ng-container>

            <!--Loading section-->
            <fl-bit
              *ngIf="!(reviewsReady$ | flAsync) || (isScrolling$ | flAsync)"
            >
              <fl-list>
                <app-review-item
                  *flRepeat="5"
                  [loading]="true"
                  [flMarginBottom]="Margin.LARGE"
                ></app-review-item>
              </fl-list>
            </fl-bit>
          </ng-container>

          <!--No reviews section-->
          <fl-bit
            *ngIf="!(reviewsCount$ | flAsync) && (reviewsReady$ | flAsync)"
            class="EmptyStateContainer"
          >
            <fl-picture
              class="EmptyStateImg"
              alt="No user reviews"
              i18n-alt="Alt text for reviews empty state"
              [display]="PictureDisplay.BLOCK"
              [flMarginBottom]="Margin.LARGE"
              [src]="'reviews/illustration.svg'"
            ></fl-picture>

            <fl-bit
              *ngIf="selectedSkillFilters$ | flAsync as selectedSkills"
              class="EmptyStateText"
              [flMarginBottom]="Margin.MID"
            >
              <fl-text
                *ngIf="selectedSkills.length > 0"
                i18n="Profile reviews empty state"
                [size]="TextSize.SMALL"
              >
                No reviews for the selected skill(s). Try another!
              </fl-text>
              <fl-text
                *ngIf="selectedSkills.length == 0"
                i18n="Profile reviews empty state"
                [size]="TextSize.SMALL"
              >
                No reviews to see here!
              </fl-text>
            </fl-bit>
          </fl-bit>
        </ng-container>
      </ng-container>

      <!--Error section-->
      <fl-bit
        *ngIf="(reviewsStatus$ | flAsync)?.error"
        class="EmptyStateContainer"
      >
        <fl-picture
          class="EmptyStateImg"
          alt="An error has occured"
          i18n-alt="Alt text for reviews error state"
          [display]="PictureDisplay.BLOCK"
          [flMarginBottom]="Margin.LARGE"
          [src]="'reviews/illustration.svg'"
        ></fl-picture>

        <fl-bit class="EmptyStateText" [flMarginBottom]="Margin.MID">
          <fl-text i18n="Profile views error state" [size]="TextSize.SMALL">
            Oops! something went wrong.
          </fl-text>
          <fl-bit i18n="Profile views error state">
            <fl-link
              flTrackingLabel="ProfileReviewsErrorRetryButton"
              (click)="retryGetFilters()"
              [size]="TextSize.SMALL"
            >
              Please try again
            </fl-link>
          </fl-bit>
        </fl-bit>
      </fl-bit>
    </fl-card>

    <!--Logged out view-->
    <ng-template #loggedOutViewProfileLink>
      <app-user-profile-logged-out-link
        [username]="(userDocument.valueChanges() | flAsync)?.username"
        [flTrackingLabel]="'LoggedOutUserProfilesViewFullProfile'"
      ></app-user-profile-logged-out-link>
    </ng-template>
  `,
  styleUrls: ['./user-profile-reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileReviewsComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  CalloutPlacement = CalloutPlacement;
  DropdownFilterContentSize = DropdownFilterContentSize;
  FontColor = FontColor;
  FontStyle = FontStyle;
  FontType = FontType;
  FontWeight = FontWeight;
  LinkColor = LinkColor;
  ListItemPadding = ListItemPadding;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  TextSize = TextSize;
  ToastAlertColor = ToastAlertColor;
  ToastAlertType = ToastAlertType;
  trackById = trackById;

  @Input() userReputation$: Rx.Observable<FreelancerReputation>;
  @Input() isCorporateProfile: boolean;
  @Input() isFeaturedReviewEditMode: boolean;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isProfileOwner: boolean;
  @Input() userDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() usersId$: Rx.Observable<number>;
  @Input() viewProfileAs$: Rx.Observable<RoleApi>;

  @Output() setFeaturedReviewEditMode = new EventEmitter<boolean>();

  // Pagination
  private pageSubject$ = new Rx.BehaviorSubject<number>(1);
  page$ = this.pageSubject$.asObservable();

  readonly SAVING_FEATURED_REVIEW_SUCCESS_MESSAGE =
    'SavingFeaturedReviewSuccessMessage';
  showSavingFeaturedReviewError = false;
  errorRequestId?: string;

  reviewsPerPage = 5;
  private pageSlice = 0;
  private readonly initialCollectionLimit = 50;
  private readonly limitIncrement = 25;
  private readonly maxCollectionLimit = 2000;
  private collectionLimit = this.initialCollectionLimit;
  private canLoadMore = false;

  // Skills and limit filters for datastore requests, batched to avoid multiple requests
  private filtersSubject$ = new Rx.BehaviorSubject<ReviewFilters>({
    limit: this.collectionLimit,
    skills: [],
  });
  filters$ = this.filtersSubject$.asObservable();

  // Review count observables
  approxTotalReviewsCount$: Rx.Observable<string>;
  reviewsCount$: Rx.Observable<number>;
  seenReviewsCount$: Rx.Observable<number>;
  reviewsOnPageCount$: Rx.Observable<number>;

  usersViewSeenReviewsCount$: Rx.Observable<number>;

  // The filtered/unfiltered review items.
  reviewItems$: Rx.Observable<readonly ReviewItem[]>;
  reviewsStatus$: Rx.Observable<RequestStatus<ReviewsCollection>>;

  // Template observables
  reviewsReady$: Rx.Observable<boolean>;
  selectedSkillFilters$: Rx.Observable<readonly ReviewSkill[]>;
  showFiltersDropdown$: Rx.Observable<boolean>;

  private viewChangeResetSubscription?: Rx.Subscription;

  reviewSkills$: Rx.Observable<readonly ReviewSkill[]>;
  isScrolling$: Rx.Observable<boolean>;

  constructor(
    private datastore: Datastore,
    private anchorScroll: AnchorScroll,
    private toastAlertService: ToastAlertService,
  ) {}

  ngOnInit(): void {
    this.viewChangeResetSubscription = this.viewProfileAs$
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        // When switching between freelancer and employer view the pagination and
        // filter settings need to be reset.
        this.resetCollectionLimit();
        this.filtersSubject$.next({ limit: this.collectionLimit, skills: [] });
        this.resetPagination();
      });

    // Get the reviews based on the selected filter and the selected page.
    const reviewsCollection = this.datastore.collection<ReviewsCollection>(
      'reviews',
      query =>
        Rx.combineLatest([this.filters$, this.viewProfileAs$]).pipe(
          map(([filters, viewProfileAs]) =>
            filters.skills.length > 0
              ? query
                  .where('toUserId', '==', this.usersId$)
                  .where('reviewType', 'in', [
                    ReviewTypeApi.CONTEST,
                    ReviewTypeApi.PROJECT,
                  ])
                  .where('role', '==', viewProfileAs)
                  .where(
                    'skillIds',
                    'intersects',
                    filters.skills.map(skill => skill.id),
                  )
                  .limit(filters.limit)
              : query
                  .where('toUserId', '==', this.usersId$)
                  .where('reviewType', 'in', [
                    ReviewTypeApi.CONTEST,
                    ReviewTypeApi.PROJECT,
                  ])
                  .where('role', '==', viewProfileAs)
                  .limit(filters.limit),
          ),
        ),
    );
    this.reviewsStatus$ = reviewsCollection.status$;

    // Get the user id of the reviewers from the reviews.
    const reviewerIds$ = reviewsCollection
      .valueChanges()
      .pipe(map(reviews => reviews.map(review => review.fromUserId)));

    // Get the reviewers
    const reviewers$ = this.datastore
      .collection<UsersCollection>('users', reviewerIds$)
      .valueChanges()
      .pipe(asObject());

    // Combine the review and reviewers user object.
    this.reviewItems$ = Rx.combineLatest([
      reviewsCollection.valueChanges(),
      reviewers$,
    ]).pipe(
      map(([reviews, reviewers]) => {
        this.canLoadMore =
          this.collectionLimit < this.maxCollectionLimit &&
          reviews.length >= this.collectionLimit;
        return reviews.map(review => ({
          review,
          fromUser: reviewers[review.fromUserId],
        }));
      }),
    );

    this.reviewsCount$ = this.reviewItems$.pipe(map(reviews => reviews.length));
    this.approxTotalReviewsCount$ = this.reviewsCount$.pipe(
      map(count =>
        count < this.collectionLimit ? `${count}` : `${this.collectionLimit}+`,
      ),
    );

    // Show filters dropdown if there are reviews returned or a filter has been applied
    this.showFiltersDropdown$ = Rx.combineLatest([
      this.reviewsStatus$,
      this.reviewItems$,
      this.filters$,
    ]).pipe(
      map(
        ([status, items, filters]) =>
          (items.length > 0 || filters.skills.length > 0) && !status.error,
      ),
    );

    this.reviewsReady$ = this.reviewsStatus$.pipe(map(status => status.ready));

    this.selectedSkillFilters$ = this.filters$.pipe(
      map(filters => filters.skills),
    );

    this.seenReviewsCount$ = this.page$.pipe(
      map(page => (page - 1) * this.reviewsPerPage),
    );

    this.reviewsOnPageCount$ = Rx.combineLatest([
      this.reviewItems$,
      this.seenReviewsCount$,
    ]).pipe(
      map(
        ([reviewItems, seenReviewsCount]) =>
          seenReviewsCount + this.sliceReviews(reviewItems).length,
      ),
    );

    this.usersViewSeenReviewsCount$ = this.seenReviewsCount$.pipe(
      map(count => count + 1),
    );
  }

  trackByReviewItem(index: number, reviewItem: ReviewItem): string | number {
    return reviewItem.review.id;
  }

  handleFilterItemsSelected(skills: readonly ReviewSkill[]): void {
    // Resets the page number to 1 and reset limits
    this.resetPagination();
    this.resetCollectionLimit();

    // This triggers the datastore call to fire again, with the new filters
    this.filtersSubject$.next({
      limit: this.collectionLimit,
      skills,
    });
  }

  handleFeaturedReviewClicked(featuredReview: Review) {
    if (!this.isFeaturedReviewEditMode) {
      // User clicked on the featured review when
      // the edit mode was off
      this.setFeaturedReviewEditMode.emit(true);
      return;
    }

    this.reviewItems$
      .pipe(
        map(reviewItems =>
          reviewItems
            .map(reviewItem => reviewItem.review)
            .find(review => (review ? review.featured : false)),
        ),
        take(1),
      )
      .toPromise()
      .then(previousFeaturedReview => {
        // We have 3 cases to be handled:
        //  - 1: We need to change the previously featured review to the selected review
        //  - 2: We need to set the selected review as featured
        //  - 3: We only need to trigger the emitter because the user clicked on an already featured review
        if (
          previousFeaturedReview &&
          previousFeaturedReview.id !== featuredReview.id
        ) {
          // Case 1.
          // Change featured review to non-featured
          this.updateFeaturedReviewStatus(
            previousFeaturedReview,
            FeaturedReviewAction.UNFEATURE,
          );
        }

        if (
          !previousFeaturedReview ||
          previousFeaturedReview.id !== featuredReview.id
        ) {
          // Case 1. or 2.
          // Change selected review to featured
          this.updateFeaturedReviewStatus(
            featuredReview,
            FeaturedReviewAction.FEATURE,
            () => {
              this.setFeaturedReviewEditMode.emit(false);
              this.anchorScroll.scrollTo(UserProfileSection.SUMMARY);
              this.toastAlertService.open(
                this.SAVING_FEATURED_REVIEW_SUCCESS_MESSAGE,
              );
            },
          );
        } else if (
          previousFeaturedReview &&
          previousFeaturedReview.id === featuredReview.id
        ) {
          // Case 3.
          this.setFeaturedReviewEditMode.emit(false);
          this.anchorScroll.scrollTo(UserProfileSection.SUMMARY);
        }
      });
  }

  private updateFeaturedReviewStatus(
    review: Review,
    featured: FeaturedReviewAction,
    callbackAfterSuccess: (() => void) | null = null,
  ) {
    this.datastore
      .document<ReviewsCollection>('reviews', review.id)
      .update({
        ...review,
        featured: featured === FeaturedReviewAction.FEATURE,
      })
      .then(response => {
        if (response.status === 'success' && callbackAfterSuccess) {
          callbackAfterSuccess();
          this.showSavingFeaturedReviewError = false;
        } else if (response.status === 'error') {
          this.showSavingFeaturedReviewError = true;
          this.errorRequestId = response.requestId;
        }
      });
  }

  getPagesCount(reviewItems: number): number {
    return Math.ceil(reviewItems / this.reviewsPerPage);
  }

  sliceReviews(reviewItems: readonly ReviewItem[]): readonly ReviewItem[] {
    return reviewItems.slice(
      this.pageSlice,
      this.pageSlice + this.reviewsPerPage,
    );
  }

  setPageSlice(page: number): void {
    this.pageSlice = page * this.reviewsPerPage - this.reviewsPerPage;
  }

  onPageSelected(page: number): void {
    this.pageSubject$.next(page);
    this.setPageSlice(page);
    this.isScrolling$ = Rx.from(
      this.anchorScroll.scrollTo({
        name: UserProfileSection.REVIEWS,
        onlyIfNeeded: true,
      }),
    ).pipe(mapTo(false), startWith(true));

    // Every fifth page load some more reviews
    Rx.combineLatest([this.seenReviewsCount$, this.selectedSkillFilters$])
      .pipe(take(1))
      .toPromise()
      .then(([seenReviewsCount, skills]) => {
        if (
          this.canLoadMore &&
          seenReviewsCount + this.reviewsPerPage >=
            this.collectionLimit - this.limitIncrement
        ) {
          this.collectionLimit += this.limitIncrement;
          this.filtersSubject$.next({
            limit: this.collectionLimit,
            skills,
          });
        }
      });
  }

  retryGetFilters(): void {
    // Re-trigger datastore call
    this.selectedSkillFilters$
      .pipe(take(1))
      .toPromise()
      .then(skills => {
        this.filtersSubject$.next({
          limit: this.collectionLimit,
          skills,
        });
      });
  }

  resetPagination(page = 1): void {
    this.pageSubject$.next(page);
    this.setPageSlice(page);
  }

  resetCollectionLimit(): void {
    this.collectionLimit = this.initialCollectionLimit;
  }

  ngOnDestroy(): void {
    if (this.viewChangeResetSubscription) {
      this.viewChangeResetSubscription.unsubscribe();
    }
  }
}
